"use client";

import { SafeTryItImg } from "@/components/customer/image-utils";
import { WebAvatar } from "@/components/customer/web-avatar";
import { CustomOrdersIcon } from "@/components/ui/CustomOrdersIcon";
import { MobileEmptyState, MobileNotLoggedInNotice } from "@/components/customer/mobile-empty-state";
import { useOrdersList, type OrderType } from "@/hooks/use-orders";
import { buildCustomerSignInPath } from "@/lib/auth-client";
import { useSession } from "@/lib/auth/use-session";
import { resolveAvatarCandidate } from "@/lib/profile-avatar";
import {
  formatMinorCurrency,
  getFirstSupportedCurrency,
} from "@shared/utils/format";
import {
  CalendarDays,
  CheckCircle,
  ChefHat,
  Clock,
  Layers,
  Receipt,
  Truck,
  Users,
  XCircle,
} from "lucide-react";
import { motion } from "motion/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

type OrdersTab = "orders" | "schedule";
type OrderStatus =
  | "pending"
  | "confirmed"
  | "preparing"
  | "ready"
  | "on-the-way"
  | "cancelled"
  | "completed"
  | "delivered"
  | "refunded";

type OrderListItem = {
  name?: string;
  dish_name?: string;
  meal_name?: string;
  image_url?: string;
  imageUrl?: string;
  image?: string;
  image_urls?: string[];
  images?: string[];
};

type GroupParticipant = {
  id: string;
  name: string;
  initials: string;
  color?: string;
  avatar?: string;
};

type GroupOrderState = {
  id: string;
  users: GroupParticipant[];
  totalUsers: number;
  isActive: boolean;
};

const getFirstCurrency = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return undefined;
};

const getPreferredCurrency = (...values: Array<string | null | undefined>) =>
  getFirstSupportedCurrency(...values) || getFirstCurrency(...values);

type OrderViewModel = {
  id: string;
  createdAtMs: number;
  scheduledForMs?: number;
  time: string;
  description: string;
  price: string;
  status: OrderStatus;
  estimatedTime?: string;
  orderNumber: string;
  items: string[];
  images: string[];
  paymentStatus?: string;
  paymentMethod?: string;
  paymentLinkToken?: string;
  isCustomOrder: boolean;
  isParallelBundle: boolean;
  parallelLabel?: string;
  groupOrder?: GroupOrderState;
  statusLabelOverride?: string;
};

const tabs: { key: OrdersTab; label: string }[] = [
  { key: "orders", label: "Orders" },
  { key: "schedule", label: "Schedule" },
];

const statusConfig = {
  pending: {
    label: "Pending",
    color: "#616161",
    bgColor: "rgba(158, 158, 158, 0.12)",
    icon: Clock,
  },
  confirmed: {
    label: "Confirmed",
    color: "#1565C0",
    bgColor: "rgba(33, 150, 243, 0.12)",
    icon: CheckCircle,
  },
  preparing: {
    label: "Preparing",
    color: "#B8860B",
    bgColor: "rgba(255, 193, 7, 0.12)",
    icon: ChefHat,
  },
  ready: {
    label: "Waiting for dispatch",
    color: "#2E7D32",
    bgColor: "rgba(76, 175, 80, 0.12)",
    icon: CheckCircle,
  },
  "on-the-way": {
    label: "On the way",
    color: "#1565C0",
    bgColor: "rgba(33, 150, 243, 0.12)",
    icon: Truck,
  },
  cancelled: {
    label: "Cancelled",
    color: "#C62828",
    bgColor: "rgba(244, 67, 54, 0.12)",
    icon: XCircle,
  },
  completed: {
    label: "Completed",
    color: "#2E7D32",
    bgColor: "rgba(76, 175, 80, 0.12)",
    icon: CheckCircle,
  },
  delivered: {
    label: "Delivered",
    color: "#2E7D32",
    bgColor: "rgba(76, 175, 80, 0.12)",
    icon: CheckCircle,
  },
  refunded: {
    label: "Refunded",
    color: "#5F6368",
    bgColor: "rgba(95, 99, 104, 0.12)",
    icon: Receipt,
  },
};

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatOrderTime(input: string | number | undefined): { label: string; timestamp: number } {
  const timestamp =
    typeof input === "number"
      ? input > 1_000_000_000_000
        ? input
        : input * 1000
      : input
        ? Date.parse(input)
        : Date.now();
  const safeTimestamp = Number.isFinite(timestamp) ? timestamp : Date.now();
  const date = new Date(safeTimestamp);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate();
  const monthName = date.toLocaleString("en-GB", { month: "long" });

  return {
    label: `${hours}:${minutes} • ${day}${getOrdinalSuffix(day)} ${monthName}`,
    timestamp: safeTimestamp,
  };
}

function parseScheduledTime(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  if (!trimmed || /^(asap|now)$/i.test(trimmed)) return undefined;

  const numericValue = Number(trimmed);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue > 1_000_000_000_000 ? numericValue : numericValue * 1000;
  }

  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeStatus(value: unknown): OrderStatus {
  const normalized = String(value || "pending").toLowerCase().replace(/\s+/g, "_");
  const statusMap: Record<string, OrderStatus> = {
    pending: "pending",
    confirmed: "confirmed",
    preparing: "preparing",
    ready: "ready",
    out_for_delivery: "on-the-way",
    on_the_way: "on-the-way",
    "on-the-way": "on-the-way",
    delivered: "delivered",
    refunded: "refunded",
    cancelled: "cancelled",
    completed: "completed",
  };
  return statusMap[normalized] ?? "pending";
}

function formatOrderId(orderId: string): string {
  const cleanId = orderId.replace(/^#/, "");
  if (cleanId.length <= 8) return `#${cleanId.toUpperCase()}`;
  return `#${cleanId.slice(-6).toUpperCase()}`;
}

function getOrderItems(order: OrderType): OrderListItem[] {
  const items = order.order_items || order.orderItems || [];
  return Array.isArray(items) ? (items as OrderListItem[]) : [];
}

function getOrderImages(items: OrderListItem[]) {
  return items
    .flatMap((item) => item.image_urls || item.images || item.image_url || item.imageUrl || item.image || [])
    .map((image) => (typeof image === "string" ? image : undefined))
    .filter((image): image is string => Boolean(image));
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function optionalIdString(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function formatInitials(name: string | undefined, fallback = "G") {
  const safeName = name?.trim();
  if (!safeName) return fallback;
  const parts = safeName.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2)).toUpperCase();
}

function getGroupOrderState(order: OrderType, status: OrderStatus): GroupOrderState | undefined {
  if (!order.is_group_order && !order.isGroupOrder && order.order_type !== "group" && order.orderType !== "group") {
    return undefined;
  }

  const groupData = (order.group_order || order.group_order_details || order.groupOrder) as
    | {
        participants?: unknown[];
        users?: unknown[];
        total_participants?: unknown;
        totalUsers?: unknown;
      }
    | undefined;
  const participants = Array.isArray(groupData?.participants)
    ? groupData.participants
    : Array.isArray(groupData?.users)
      ? groupData.users
      : [];
  const users = participants
    .map((participant): GroupParticipant | undefined => {
      if (!participant || typeof participant !== "object") return undefined;
      const record = participant as Record<string, unknown>;
      const id = optionalIdString(record.user_id || record.id);
      const name = optionalString(record.user_name || record.name) || "Guest";
      const user: GroupParticipant = {
        id: id || name,
        name,
        initials: optionalString(record.user_initials || record.initials)?.slice(0, 2).toUpperCase() || formatInitials(name),
        color: optionalString(record.user_color || record.color),
        avatar: resolveAvatarCandidate(record.avatar_url || record.avatar),
      };
      return user;
    })
    .filter((participant): participant is GroupParticipant => Boolean(participant));

  const totalUsers =
    typeof groupData?.total_participants === "number"
      ? groupData.total_participants
      : typeof groupData?.totalUsers === "number"
        ? groupData.totalUsers
        : users.length;

  return {
    id: optionalIdString(order.group_order_id || order.groupOrderId || order._id || order.id || order.order_id) || "group-order",
    users,
    totalUsers: Math.max(totalUsers, users.length),
    isActive: status !== "cancelled" && status !== "completed" && status !== "delivered" && status !== "refunded",
  };
}

function toOrderViewModel(order: OrderType): OrderViewModel {
  const id = String(order._id || order.id || order.order_id || "");
  const rawDate = order.order_date || order.orderDate || order.createdAt || order._creationTime;
  const { label: time, timestamp } = formatOrderTime(rawDate);
  const items = getOrderItems(order);
  const itemNames = items
    .map((item) => item.name || item.meal_name || item.dish_name || "Item")
    .filter(Boolean);
  const foodCreatorName =
    typeof order.foodCreator_name === "string"
      ? order.foodCreator_name
      : typeof order.restaurant_name === "string"
        ? order.restaurant_name
        : undefined;
  const isPlatformSpillOrder = order.is_platform_spill_order === true;
  const platformSpillStatus =
    typeof order.platform_spill_status === "string"
      ? order.platform_spill_status.trim().toLowerCase()
      : "";
  const platformSpillDescriptionSuffix =
    !isPlatformSpillOrder
      ? ""
      : platformSpillStatus === "accepted"
        ? ". A nearby creator accepted it and is getting started."
      : platformSpillStatus === "failed"
          ? ". We could not match a nearby creator. If payment was captured, we will cancel and refund it automatically."
          : platformSpillStatus === "offered"
            ? ". A nearby creator is reviewing it now. If they pass or time runs out, we will keep trying others."
            : platformSpillStatus === "searching"
              ? ". Checking nearby creators now. You do not need to do anything."
            : "";
  const description =
    itemNames.length > 0
      ? `${itemNames.slice(0, 2).join(", ")}${itemNames.length > 2 ? `, +${itemNames.length - 2} more` : ""}${
          foodCreatorName ? ` from ${foodCreatorName}` : ""
        }${platformSpillDescriptionSuffix}`
      : foodCreatorName
        ? `Order from ${foodCreatorName}${platformSpillDescriptionSuffix}`
        : `Order${platformSpillDescriptionSuffix}`;
  const scheduledForMs = parseScheduledTime(order.scheduled_for || order.delivery_time);
  const estimatedTime =
    typeof order.estimated_delivery_time === "string"
      ? order.estimated_delivery_time
      : typeof order.estimated_prep_time_minutes === "number"
        ? `${order.estimated_prep_time_minutes}-${order.estimated_prep_time_minutes + 5} min`
        : scheduledForMs && scheduledForMs > Date.now()
          ? `Scheduled ${new Date(scheduledForMs).toLocaleString("en-GB", {
              weekday: "short",
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : undefined;
  const status = normalizeStatus(order.order_status || order.orderStatus || order.status);
  const paymentStatus = order.payment_status || order.paymentStatus;
  const paymentMethod = optionalString(order.payment_method || order.paymentMethod)?.toLowerCase();
  const paymentLinkToken = optionalString(order.payment_link_token || order.paymentLinkToken);
  const bundleOrderIndex = typeof order.bundle_order_index === "number" ? order.bundle_order_index : undefined;
  const bundleOrderCount = typeof order.bundle_order_count === "number" ? order.bundle_order_count : undefined;
  const isParallelBundle = Boolean(order.checkout_bundle_id) && Number(order.bundle_order_count || 0) > 1;
  const displayTotalAmount = optionalNumber(
    (order as OrderType & { displayTotalAmount?: unknown }).displayTotalAmount,
  );
  const orderAmount = displayTotalAmount ?? order.total_amount ?? order.totalAmount;
  const orderCurrency = getPreferredCurrency(
    displayTotalAmount !== undefined
      ? typeof order.displayCurrency === "string"
        ? order.displayCurrency
        : typeof order.nativeCurrency === "string"
          ? order.nativeCurrency
        : typeof order.currency === "string"
          ? order.currency
        : undefined
      : typeof order.nativeCurrency === "string"
        ? order.nativeCurrency
        : typeof order.currency === "string"
          ? order.currency
        : undefined,
  );

  return {
    id,
    createdAtMs: timestamp,
    scheduledForMs,
    time,
    description,
    price:
      typeof orderAmount !== "number" || !Number.isFinite(orderAmount)
        ? "Price unavailable"
        : !orderCurrency
          ? "Amount captured · currency unavailable"
          : (() => {
              const normalizedCurrency = orderCurrency.trim().toUpperCase();
              return formatMinorCurrency(orderAmount, {
                currency: normalizedCurrency,
                fallback: "Price unavailable",
              });
            })(),
    status,
    estimatedTime,
    orderNumber: formatOrderId(order.order_id || id),
    items: itemNames,
    images: getOrderImages(items),
    paymentStatus,
    paymentMethod,
    paymentLinkToken,
    isCustomOrder:
      order.is_custom_order === true ||
      order.isCustomOrder === true ||
      order.order_type === "custom" ||
      Boolean(order.custom_order_id),
    isParallelBundle,
    parallelLabel:
      isParallelBundle && bundleOrderIndex && bundleOrderCount
        ? `Parallel ${bundleOrderIndex}/${bundleOrderCount}`
        : isParallelBundle
          ? "Parallel"
          : undefined,
    groupOrder: getGroupOrderState(order, status),
    statusLabelOverride:
      isPlatformSpillOrder && status === "pending"
        ? platformSpillStatus === "accepted"
          ? "Creator matched"
          : platformSpillStatus === "failed"
            ? "Match unavailable"
            : platformSpillStatus === "offered" || platformSpillStatus === "searching"
              ? "Finding creator"
              : undefined
        : undefined,
  };
}

function OrderStatusBadge({
  status,
  paymentStatus,
  labelOverride,
}: {
  status: OrderStatus;
  paymentStatus?: string;
  labelOverride?: string;
}) {
  const normalizedPaymentStatus = paymentStatus?.trim().toLowerCase();
  const config =
    status === "pending" && normalizedPaymentStatus === "failed"
      ? {
          label: "Payment Failed",
          color: "#DC2626",
          bgColor: "rgba(220, 38, 38, 0.12)",
          icon: XCircle,
        }
      : status === "pending" && normalizedPaymentStatus === "pending"
      ? {
          label: "Waiting for Payment",
          color: "#7C3AED",
          bgColor: "rgba(124, 58, 237, 0.12)",
          icon: Receipt,
        }
      : statusConfig[status];
  const Icon = config.icon;

  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[rgba(17,24,39,0.08)] px-2 py-[5px] text-[11px] font-semibold leading-none shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
      style={{ backgroundColor: config.bgColor, color: config.color }}
    >
      <Icon className="h-3 w-3" />
      {labelOverride || config.label}
    </span>
  );
}

function OrderImageStack({ images }: { images: string[] }) {
  if (images.length === 0) {
    return (
      <span className="ml-1 mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center text-[#094327]">
        <CustomOrdersIcon size={32} color="#094327" />
      </span>
    );
  }

  return (
    <span className="ml-1 mt-0.5 flex h-11 w-[58px] shrink-0 items-center">
      {images.slice(0, 4).map((image, index) => (
        <SafeTryItImg
          key={`${image}-${index}`}
          src={image}
          alt=""
          aria-hidden="true"
          containerClassName="relative block h-10 w-10 overflow-hidden rounded-full border-2 border-[rgba(255,255,255,0.8)] shadow-[0_6px_14px_rgba(0,0,0,0.08)]"
          className="h-full w-full object-cover"
          containerStyle={{ marginLeft: index === 0 ? 0 : -24, zIndex: 4 - index }}
        />
      ))}
    </span>
  );
}

function GroupOrderPreview({
  groupOrder,
  compact = false,
}: {
  groupOrder?: GroupOrderState;
  compact?: boolean;
}) {
  if (!groupOrder) return null;

  const visibleUsers = groupOrder.users.slice(0, 4);
  const overflowCount = Math.max(0, groupOrder.totalUsers - visibleUsers.length);

  return (
    <div className={`${compact ? "" : "mt-2"} flex items-center gap-2 font-satoshi text-[12px] font-semibold text-[#0F5132]`}>
      {!compact ? <Users className="h-3.5 w-3.5" /> : null}
      <div className="flex h-6 items-center">
        {visibleUsers.length > 0 ? (
          visibleUsers.map((user, userIndex) => (
            <span
              key={`${groupOrder.id}-${user.id}-${userIndex}`}
              className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white shadow-sm"
              style={{
                backgroundColor: user.color || ["#DC2626", "#094327", "#2563EB", "#7C3AED"][userIndex % 4],
                marginLeft: userIndex === 0 ? 0 : -8,
                zIndex: 5 - userIndex,
              }}
              title={user.name}
            >
              {user.avatar ? (
                <WebAvatar
                  alt={user.name}
                  src={user.avatar}
                  imageClassName="rounded-full object-cover"
                />
              ) : (
                user.initials
              )}
            </span>
          ))
        ) : (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#094327] text-[10px] font-bold text-white">
            G
          </span>
        )}
        {overflowCount > 0 ? (
          <span className="-ml-2 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-[#031D11] px-1.5 text-[10px] font-bold text-white">
            +{overflowCount}
          </span>
        ) : null}
      </div>
      {!compact ? <span>{groupOrder.isActive ? "Group order active" : "Group order"}</span> : null}
    </div>
  );
}

function getOrderHref(order: OrderViewModel) {
  const encodedId = encodeURIComponent(order.id);
  if (order.isCustomOrder) return `/order-details?id=${encodedId}`;
  if (canSharePaymentLink(order)) {
    return `/orders/payment-request?order_id=${encodedId}`;
  }
  return `/order-details?id=${encodedId}`;
}

function canSharePaymentLink(order: OrderViewModel) {
  return (
    order.status === "pending" &&
    order.paymentStatus?.trim().toLowerCase() === "pending" &&
    order.paymentMethod === "pay_for_me" &&
    Boolean(order.paymentLinkToken)
  );
}

function MobileOrderCard({
  order,
  index,
  showSeparator,
}: {
  order: OrderViewModel;
  index: number;
  showSeparator: boolean;
}) {
  const isOngoingOrder =
    order.status !== "cancelled" && order.status !== "completed" && order.status !== "delivered" && order.status !== "refunded";
  const href = getOrderHref(order);
  const showVisibleStatus = order.status === "ready";

  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.04, duration: 0.34 }}
    >
      <Link href={href} className="block">
        <article className="mx-0.5 flex items-start gap-3 rounded-[14px] border border-[rgba(17,24,39,0.08)] bg-[#FFFDFB] px-3 py-3 transition hover:bg-white">
          <OrderImageStack images={order.images} />
          <div className="min-w-0 flex-1">
            <div className="mb-[3px] flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 pr-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="truncate font-satoshi text-[14px] font-medium text-[#687076]">
                    {order.time}
                  </p>
                  <p className="font-satoshi text-[12px] font-semibold tracking-[0.04em] text-[#6B7280]">
                    {order.orderNumber}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 md:max-w-[58%]">
                {order.isParallelBundle ? (
                  <span
                    className="inline-flex h-[18px] w-[18px] items-center justify-center text-[#92400E]"
                    aria-label="Creator bundle order"
                    title="Creator bundle order"
                  >
                    <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                ) : null}
                {order.groupOrder ? <GroupOrderPreview groupOrder={order.groupOrder} compact /> : null}
                {!showVisibleStatus ? (
                  <OrderStatusBadge
                    status={order.status}
                    paymentStatus={order.paymentStatus}
                    labelOverride={order.statusLabelOverride}
                  />
                ) : null}
              </div>
            </div>

            <h2 className="mb-1.5 font-satoshi text-[16px] font-semibold leading-5 text-[#11181C]">
              {order.description}
            </h2>

            {showVisibleStatus ? (
              <div className="mb-2 space-y-1.5">
                <div className="relative h-1.5 overflow-hidden">
                  <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[rgba(255,59,48,0.18)]" />
                  <div className="absolute left-0 top-1/2 h-[5px] w-[72px] -translate-y-1/2 rounded-full bg-[linear-gradient(90deg,rgba(255,255,255,0),#FF3B30,#FF3B30,rgba(255,255,255,0))]" />
                </div>
                <p className="font-satoshi text-[12px] font-medium leading-4 text-[#6B7280]">
                  Waiting for dispatch. We are finding a courier for this order.
                </p>
              </div>
            ) : null}

            {isOngoingOrder && order.items.length > 0 ? (
              <div className="mb-1.5 space-y-0.5 font-satoshi text-[13px] leading-4 text-[#374151]">
                <p>
                  <span className="font-medium text-[#6B7280]">Items: </span>
                  • {order.items[0]}
                </p>
                {order.items[1] ? <p>• {order.items[1]}</p> : null}
                {order.items.length > 2 ? (
                  <p className="text-[12px] italic text-[#9CA3AF]">
                    +{order.items.length - 2} more items
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 font-satoshi text-[16px] font-bold text-[#166534]">{order.price}</p>
              <div className="min-w-0 shrink">
                {order.estimatedTime ? (
                  <p className="flex items-center gap-1 whitespace-nowrap font-satoshi text-[13px] font-medium text-[#687076]">
                    <Clock className="h-3 w-3" />
                    {order.estimatedTime}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </article>
      </Link>
      {showSeparator ? <div className="mx-3 mt-2 h-px bg-[rgba(229,231,235,0.5)]" /> : null}
    </motion.div>
  );
}

function EmptyOrdersState({
  title,
  subtitle,
  icon,
  illustrationSrc,
  illustrationAlt = "",
  actionLabel,
  actionHref,
  secondaryActionLabel,
  secondaryActionHref,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  illustrationSrc?: string;
  illustrationAlt?: string;
  actionLabel: string;
  actionHref: string;
  secondaryActionLabel?: string;
  secondaryActionHref?: string;
}) {
  return (
    <MobileEmptyState
      iconElement={icon}
      illustrationSrc={illustrationSrc}
      illustrationAlt={illustrationAlt}
      illustrationClassName="mb-1 h-[164px] w-[164px]"
      title={title}
      subtitle={subtitle}
      action={{ label: actionLabel, href: actionHref }}
      secondaryAction={
        secondaryActionLabel && secondaryActionHref
          ? { label: secondaryActionLabel, href: secondaryActionHref }
          : undefined
      }
      className="min-h-[58vh] rounded-[30px] border border-white/60 bg-white/78 shadow-[0_24px_64px_rgba(17,24,39,0.08)] backdrop-blur-xl"
    />
  );
}

export default function OrdersPage() {
  const [activeTab, setActiveTab] = useState<OrdersTab>("orders");
  const [currentTimeMs, setCurrentTimeMs] = useState<number | null>(null);
  const { isAuthenticated, isLoading } = useSession();
  const pathname = usePathname();
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
  } = useOrdersList({
    limit: 50,
    status: "all",
    order_type: "all",
  });

  const allOrders = useMemo(
    () =>
      (ordersData?.orders || [])
        .map(toOrderViewModel)
        .sort((a, b) => b.createdAtMs - a.createdAtMs),
    [ordersData?.orders],
  );

  useEffect(() => {
    setCurrentTimeMs(Date.now());
  }, [ordersData?.orders]);

  const scheduledOrders = useMemo(
    () =>
      allOrders
        .filter(
          (order) =>
            order.scheduledForMs &&
            currentTimeMs !== null &&
            order.scheduledForMs > currentTimeMs &&
            order.status !== "cancelled" &&
            order.status !== "completed" &&
            order.status !== "delivered" &&
            order.status !== "refunded",
        )
        .sort((a, b) => (a.scheduledForMs || 0) - (b.scheduledForMs || 0)),
    [allOrders, currentTimeMs],
  );
  const visibleOrders = activeTab === "schedule" ? scheduledOrders : allOrders;

  if (isLoading) {
    return (
      <OrdersShell activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="pt-2">
          <SectionHeader title={activeTab === "schedule" ? "Scheduled Orders" : "All Orders"} />
          {Array.from({ length: 3 }).map((_, index) => (
            <OrderSkeleton key={index} />
          ))}
        </div>
      </OrdersShell>
    );
  }

  return (
    <OrdersShell activeTab={activeTab} onTabChange={setActiveTab}>
      {!isAuthenticated ? (
        <div className="grid items-start gap-4 px-[2px] py-4 lg:min-h-[58vh] lg:grid-cols-[minmax(0,0.9fr)_minmax(300px,0.7fr)] lg:items-center lg:gap-10 lg:px-6 lg:py-16">
          <div className="hidden max-w-[34rem] lg:block">
            <h2 className="text-[29px] font-bold leading-[33px] text-black sm:text-[42px] sm:leading-[0.96] sm:text-[#094327]">
              Keep every delivery, reorder, and schedule in one place.
            </h2>
            <p className="mt-3 max-w-[30rem] text-[16px] leading-7 text-slate-600 sm:mt-5 sm:font-satoshi sm:text-[17px] sm:leading-8 sm:text-[#516066]">
              Sign in to track active deliveries, check scheduled meals, and revisit past orders without bouncing between screens.
            </p>
          </div>

          <div className="w-full lg:justify-self-end lg:max-w-[420px]">
            <MobileNotLoggedInNotice
              title="Sign in to see your orders"
              subtitle="Track active deliveries and revisit past orders once you're signed in."
              buttonLabel="Sign In"
              onSignInPress={() => {
                window.location.href = buildCustomerSignInPath(pathname || "/orders");
              }}
              className="px-3 py-0 sm:px-4 sm:py-5 lg:rounded-[28px] lg:border lg:border-white/70 lg:bg-white/78 lg:p-7 lg:shadow-[0_20px_60px_rgba(17,24,39,0.08)] lg:backdrop-blur-xl"
              titleClassName="text-[24px] leading-[1.08] sm:text-[28px]"
              subtitleClassName="max-w-[26rem] text-[#516066]"
            />
          </div>
        </div>
      ) : ordersLoading ? (
        <div className="pt-2">
          <SectionHeader title={activeTab === "schedule" ? "Scheduled Orders" : "All Orders"} />
          {Array.from({ length: 3 }).map((_, index) => (
            <OrderSkeleton key={index} />
          ))}
        </div>
      ) : ordersError ? (
        <EmptyOrdersState
          title="We couldn't load your orders"
          subtitle="The request took too long after sign-in. Reload the page and try again."
          icon={<Receipt className="h-14 w-14 text-[#10B981]" />}
          actionLabel="Reload Orders"
          actionHref="/orders"
        />
      ) : visibleOrders.length === 0 ? (
        <EmptyOrdersState
          title={activeTab === "schedule" ? "No meals scheduled yet" : "Place your first order to see orders here"}
          subtitle={
            activeTab === "schedule"
              ? "Pick a meal, choose a delivery time at checkout, and your upcoming orders will show here."
              : "Browse food creators and meals to get started with your first order"
          }
          icon={
            activeTab === "schedule" ? (
              <CalendarDays className="h-14 w-14 text-[#10B981]" />
            ) : (
              <SafeTryItImg
                alt=""
                src="/Mascot%20emotion%20library/Cribnosh_mascot_multiple_202604051300.jpeg"
                aria-hidden="true"
                containerClassName="relative block h-[150px] w-[150px] overflow-hidden rounded-full"
                className="h-full w-full object-cover"
              />
            )
          }
          illustrationSrc={activeTab === "schedule" ? "/mobile-illustrations/scheduled-orders-empty.png" : "/mobile-illustrations/orders-empty-flat.png"}
          illustrationAlt=""
          actionLabel={activeTab === "schedule" ? "Browse Meals" : "Browse creators"}
          actionHref="/customer"
          secondaryActionLabel={activeTab === "schedule" ? "Start Group Order" : undefined}
          secondaryActionHref={activeTab === "schedule" ? "/orders/group/create" : undefined}
        />
      ) : (
        <div className="pt-2">
          <SectionHeader title={activeTab === "schedule" ? "Scheduled Orders" : "All Orders"} />
          <div>
            {visibleOrders.map((order, index) => (
              <MobileOrderCard
                key={order.id || index}
                order={order}
                index={index}
                showSeparator={index < visibleOrders.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </OrdersShell>
  );
}

function OrdersShell({
  children,
  activeTab = "orders",
  onTabChange,
}: {
  children: React.ReactNode;
  activeTab?: OrdersTab;
  onTabChange?: (tab: OrdersTab) => void;
}) {
  return (
    <main className="relative min-h-screen overflow-x-clip bg-[linear-gradient(135deg,#f8e6f0_0%,#faf2e8_100%)] pb-[7.5rem] text-[#11181C] md:pb-24">
      <div className="relative mx-auto w-full max-w-6xl px-[10px] pb-8 pt-[calc(env(safe-area-inset-top)+18px)] md:px-6 md:pb-12 md:pt-8">
        <header className="pb-4 md:pb-6 lg:flex lg:items-end lg:justify-between lg:gap-8">
          <div className="px-[2px] lg:px-0">
            <h1 className="text-[29px] font-bold leading-[33px] text-black md:max-w-[460px] md:text-[34px] md:leading-[38px]">
              Orders
            </h1>
          </div>

          <div className="mt-4 flex gap-7 px-[2px] lg:mt-0 lg:gap-3 lg:px-0">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    className={`relative inline-flex min-h-0 items-center justify-center pb-2 text-[16px] leading-5 tracking-[-0.02em] transition ${
                      isActive
                        ? "font-semibold text-[#11181C]"
                        : "font-medium text-[#687076]"
                    } lg:min-h-9 lg:min-w-[96px] lg:rounded-full lg:px-5 lg:pb-0 lg:text-sm lg:tracking-normal ${
                      isActive
                        ? "lg:bg-[#094327] lg:text-white"
                        : "lg:border lg:border-slate-200 lg:bg-white lg:text-slate-700"
                    }`}
                    type="button"
                    onClick={() => onTabChange?.(tab.key)}
                  >
                    {tab.label}
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#DC2626] transition lg:hidden ${
                        isActive ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
                      }`}
                    />
                  </button>
                );
              })}
          </div>
        </header>

        {children}
      </div>
    </main>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="mb-4 px-[2px] pt-1">
      <p className="hidden text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 md:block">Orders</p>
      <h2 className="mt-1 text-[18px] font-bold leading-6 tracking-[-0.02em] text-[#11181C] lg:font-asgard lg:text-[22px] lg:leading-7 lg:text-[#094327]">
        {title}
      </h2>
    </div>
  );
}

function OrderSkeleton() {
  return (
    <div className="mb-3 rounded-[24px] bg-white/72 p-4 shadow-[0_14px_36px_rgba(17,24,39,0.05)]">
      <div className="flex animate-pulse items-start gap-3">
        <div className="h-12 w-12 rounded-[16px] bg-[#f5ebe6]" />
        <div className="flex-1 space-y-3">
          <div className="h-3 w-2/5 rounded-full bg-[#f2e5df]" />
          <div className="h-4 w-4/5 rounded-full bg-[#efe4de]" />
          <div className="h-3 w-3/5 rounded-full bg-[#f6ece7]" />
        </div>
      </div>
    </div>
  );
}
